import {
  isTableOnHold,
  isTableProcessing,
  isTableOfflineSoldOut,
  normalizeTableSheetStatus,
} from "./tableSheet";

/** Index public sheet tables for lookup by tableId / sectionId:tableId. */
export const indexLiveSheetTables = (sheet) => {
  const byTableId = new Map();
  const bySectionTable = new Map();
  const bySectionId = new Map();

  for (const section of sheet?.sections ?? []) {
    const sectionId = String(section?.sectionId ?? "").trim();
    const tables = Array.isArray(section?.tables) ? section.tables : [];
    if (sectionId) bySectionId.set(sectionId, tables);

    for (const table of tables) {
      const tableId = String(table?.tableId ?? "").trim();
      if (!tableId) continue;
      byTableId.set(tableId, table);
      if (sectionId) bySectionTable.set(`${sectionId}:${tableId}`, table);
    }
  }

  return { byTableId, bySectionTable, bySectionId };
};

export const resolveLiveSheetTable = (row, liveIndex) => {
  if (!row || !liveIndex) return null;
  const areaId = String(row.areaId ?? "").trim();
  const tableId = String(row.tableId ?? "").trim();

  if (tableId) {
    if (areaId) {
      const scoped = liveIndex.bySectionTable.get(`${areaId}:${tableId}`);
      if (scoped) return scoped;
    }
    return liveIndex.byTableId.get(tableId) || null;
  }

  if (areaId) {
    const sectionTables = liveIndex.bySectionId.get(areaId) || [];
    if (sectionTables.length === 1) return sectionTables[0];
  }
  return null;
};

export const liveStatusKeyFromSheetTable = (sheetTable) => {
  if (!sheetTable) return null;
  // PENDING_PAYMENT processing has priority over sold_out / Hold.
  if (isTableProcessing(sheetTable)) return "processing";
  if (isTableOnHold(sheetTable)) return "hold";
  const status = normalizeTableSheetStatus(sheetTable.status);
  if (status === "booked") return "booked";
  if (status === "unavailable") return "unavailable";
  if (status === "processing") return "processing";
  if (status === "sold_out" || isTableOfflineSoldOut(sheetTable)) {
    return "sold_out";
  }
  if (status === "available") return "available";
  return status || null;
};

/** Map live booking status → Manual availability button. */
export const liveStatusToManualAvailability = (liveKey) => {
  // Keep processing distinct from sold_out for live highlight; still blocks booking.
  if (liveKey === "booked" || liveKey === "sold_out") return "SOLD_OUT";
  if (
    liveKey === "hold" ||
    liveKey === "unavailable" ||
    liveKey === "processing"
  ) {
    return "UNAVAILABLE";
  }
  if (liveKey === "available") return "AUTO";
  return null;
};

export const summarizeZoneLive = (zoneTables, liveIndex) => {
  const counts = {
    booked: 0,
    sold_out: 0,
    processing: 0,
    hold: 0,
    unavailable: 0,
    available: 0,
    known: 0,
    total: zoneTables.length,
  };

  for (const table of zoneTables) {
    const key = liveStatusKeyFromSheetTable(
      resolveLiveSheetTable(table, liveIndex)
    );
    if (!key) continue;
    counts.known += 1;
    if (counts[key] != null) counts[key] += 1;
  }

  return counts;
};

export const zoneLiveToManualAvailability = (summary) => {
  if (!summary?.known) return null;

  const taken = summary.booked + summary.sold_out;
  const blocked =
    summary.hold + summary.unavailable + (summary.processing || 0);

  if (taken >= summary.known) return "SOLD_OUT";
  if (blocked >= summary.known) return "UNAVAILABLE";
  if (taken > 0 && summary.available === 0) {
    return blocked > 0 ? "UNAVAILABLE" : "SOLD_OUT";
  }
  if (summary.available === summary.known) return "AUTO";
  if (summary.available > 0) return "AUTO";
  return null;
};

/**
 * Resolve which Manual availability button to highlight.
 * Live booking state wins when known; otherwise saved override.
 */
export const resolveDisplayedManualAvailability = (
  row,
  liveIndex,
  savedAvailability,
  nestedTables = null
) => {
  let liveAvailability = null;
  if (Array.isArray(nestedTables) && nestedTables.length > 0) {
    liveAvailability = zoneLiveToManualAvailability(
      summarizeZoneLive(nestedTables, liveIndex)
    );
  } else {
    liveAvailability = liveStatusToManualAvailability(
      liveStatusKeyFromSheetTable(resolveLiveSheetTable(row, liveIndex))
    );
  }
  return liveAvailability || savedAvailability;
};
